import React, { ChangeEventHandler, FormEventHandler, useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';

import { Box, Button, Container, Stack } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import OfferAssociationsSearchForm from '~/components/Common/Forms/helpers/OfferAssociationsSearchForm';
import Spinner from '~/components/Common/Spinner';
import AddManualExperienceLocationModal from '~/components/Content/ManualExperienceLocations/AddManualExperienceLocationModal';
import { ManualExperienceLocation } from '~/components/Content/ManualExperienceLocations/Columns';
import ConfirmDeleteModal from '~/components/Content/ManualExperienceLocations/ConfirmDeleteModal';
import ManualExperienceLocationTable from '~/components/Content/ManualExperienceLocations/ManualExperienceLocationTable';

import { getExperienceOfferById } from '~/services/ExperiencesService';
import SearchService from '~/services/SearchService';
import { formatDateWithClock } from '~/services/TimeService';

function ManualExperienceLocationList() {
  const [searchString, setSearchString] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(false);
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);
  const [data, setData] = useState<Array<ManualExperienceLocation>>([]);
  const [displayData, setDisplayData] = useState<Array<ManualExperienceLocation>>([]);
  const [isAddModalOpen, setAddModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [itemsToDelete, setItemsToDelete] = useState<Array<ManualExperienceLocation>>([]);

  const { enqueueSnackbar } = useSnackbar();

  const handleSearchTextChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchString(e.target.value);
  };

  useEffect(() => {
    if (!searchString) {
      setDisplayData(data); // Reset to original list if searchString is empty
    } else {
      const lowerCaseSearchString = searchString.trim().toLowerCase();
      setDisplayData(
        data.filter(
          (item) =>
            item.experienceOfferName.toLowerCase().includes(lowerCaseSearchString) ||
            item.experienceOfferId.toLowerCase().includes(lowerCaseSearchString) ||
            item.placeName.toLowerCase().includes(lowerCaseSearchString) ||
            item.placeId.toLowerCase().includes(lowerCaseSearchString) ||
            item.comments.toLowerCase().includes(lowerCaseSearchString) ||
            item.addedBy.toLowerCase().includes(lowerCaseSearchString),
        ),
      );
    }
  }, [searchString, data]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { result } = await SearchService.getExperienceManualLocations();

      // Fetch place names and experience offer names
      const dataWithNames = await Promise.all(
        result.map(async ({ experienceOfferId, placeId, comments, addedBy, createdAt }) => {
          let placeName = placeId;
          let experienceOfferName = experienceOfferId;

          try {
            // Fetch place name
            const placeResponse = await SearchService.getPlace(placeId);
            placeName = placeResponse.result.name;
          } catch (e) {
            console.warn(`Failed to fetch place name for ${placeId}:`, e);
          }

          try {
            // Fetch experience offer name using ExperiencesService
            const experienceResponse = await getExperienceOfferById({
              id: experienceOfferId,
              brand: 'luxuryescapes',
            });
            experienceOfferName = experienceResponse.result.title;
          } catch (e) {
            console.warn(`Failed to fetch experience offer name for ${experienceOfferId}:`, e);
          }

          return {
            id: `${experienceOfferId}-${placeId}`,
            experienceOfferName,
            experienceOfferId,
            placeName,
            placeId,
            comments: comments ?? '',
            addedBy,
            createdAt: formatDateWithClock(createdAt),
          };
        }),
      );

      setData(dataWithNames);
      setDisplayData(dataWithNames);
    } catch (e) {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      e.preventDefault();
      setSearchString(searchString);
    },
    [searchString],
  );

  const handleSelect = useCallback((ids: GridRowSelectionModel) => {
    setSelected(ids);
  }, []);

  const handleDelete = useCallback(async () => {
    const deletePromises = selected.map(async (id: string) => {
      const itemToBeRemoved = data.find((item) => item.id === id);
      try {
        const { status } = await SearchService.deleteExperienceManualLocation(
          itemToBeRemoved.experienceOfferId,
          itemToBeRemoved.placeId,
        );

        if (status === 200) {
          enqueueSnackbar(`Successfully deleted association for ${itemToBeRemoved.experienceOfferName}`, {
            variant: 'success',
          });
        } else {
          enqueueSnackbar(`Failed to delete association for ${itemToBeRemoved.experienceOfferName}`, {
            variant: 'error',
          });
        }
      } catch (error) {
        enqueueSnackbar(`Error deleting association for ${itemToBeRemoved.experienceOfferName}`, { variant: 'error' });
      }
    });

    await Promise.all(deletePromises);
    await fetchData();
    setItemsToDelete([]);
  }, [selected, fetchData, data, enqueueSnackbar]);

  const handleAddOpenModal = () => {
    setAddModalOpen(true);
  };

  const handleAddCloseModal = async () => {
    setAddModalOpen(false);
    await fetchData();
  };

  const handleConfirmDeleteCloseModal = async () => {
    setDeleteModalOpen(false);
  };

  const handleConfirmDeleteOpenModal = async () => {
    const selectedIds = selected.map((id: string) => id);
    setItemsToDelete(selectedIds.map((id) => data.find((item) => item.id === id)));
    setDeleteModalOpen(true);
  };

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Manual Experience Locations</title>
      </Helmet>

      <Stack direction="column" gap={4}>
        <AddManualExperienceLocationModal data={data} open={isAddModalOpen} onClose={handleAddCloseModal} />
        <ConfirmDeleteModal
          itemsToRemove={itemsToDelete}
          open={isDeleteModalOpen}
          handleDelete={handleDelete}
          onClose={handleConfirmDeleteCloseModal}
        />

        <PageSubheader title="Manual Experience Locations" />

        <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1.25}>
          <Box width="50%">
            <OfferAssociationsSearchForm
              searchString={searchString}
              handleChange={handleSearchTextChange}
              handleSubmit={handleSearchSubmit}
            />
          </Box>
          <Button sx={{ width: '30%' }} variant="contained" color="primary" onClick={handleAddOpenModal}>
            Add New Association
          </Button>
        </Box>

        {isLoading && <Spinner />}

        <ManualExperienceLocationTable
          selected={selected}
          isLoading={isLoading}
          data={displayData}
          handleSelect={handleSelect}
          handleDelete={handleConfirmDeleteOpenModal}
        />
      </Stack>
    </Container>
  );
}

export default ManualExperienceLocationList;
